import { useState } from "react";
import { PencilIcon } from 'lucide-react';
import Section from "../Section";
import BannerSectionStyle2 from "../Section/BannerSection/BannerSectionStyle2";
import AdminTable, { cellClass } from "../Admin/AdminTable";
import { editService, getErrorMessage, getServices } from "../../helpers/lib/axios";
import { useApiList } from "../../helpers/lib/useApiData";
import { useAppToast } from "../../helpers/lib/useAppToast";

// Only these fields are editable; server-managed fields (_id, timestamps, ...) are never sent back.
const EDITABLE_FIELDS = ['name', 'location', 'type', 'price', 'sessions', 'isExtra'];

const formatPrice = (price) => {
    const amount = Number(price);
    return price === null || price === undefined || price === '' || Number.isNaN(amount)
        ? '—'
        : `₦${amount.toLocaleString()}`;
};

const Services = () => {
    const toast = useAppToast()
    const { items, loading, error, reload } = useApiList(getServices, 'Failed to load services.');
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked :
                type === 'number' ? Number(value) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = Object.fromEntries(
            EDITABLE_FIELDS.filter((key) => key in editForm).map((key) => [key, editForm[key]])
        );
        setSaving(true);
        try {
            await editService(editForm._id, payload);
            toast({ status: 'success', description: 'Service updated successfully' });
            setEditForm(null);
            reload();
        } catch (err) {
            toast({ status: 'error', description: getErrorMessage(err, 'Failed to update service') });
        } finally {
            setSaving(false);
        }
    };

    return (<>
        <BannerSectionStyle2
            bgUrl="/images/about/banner_bg.svg"
            imgUrl="/images/about/banner_img.png"
            title="Services"
            subTitle="Services information"
        />
        <Section
            topMd={170}
            topLg={150}
            topXl={110}
            bottomMd={200}
            bottomLg={150}
            bottomXl={110}
            className='flex justify-center items-center w-full'
        >
            <div className="lg:max-w-[70%] w-full mt-[5%] p-4">
                <AdminTable
                    columns={['Name', 'Location', 'Type', 'Price', 'Sessions', 'Extra', 'Actions']}
                    items={items}
                    loading={loading}
                    error={error}
                    emptyText="No services yet."
                    getKey={(service) => service?._id}
                    renderRow={(service) => (<>
                        <td className={cellClass}>{service.name}</td>
                        <td className={cellClass}>{service.location}</td>
                        <td className={cellClass}>{service.type}</td>
                        <td className={cellClass}>{formatPrice(service.price)}</td>
                        <td className={cellClass}>{service.sessions}</td>
                        <td className={cellClass}>{service.isExtra ? 'Yes' : 'No'}</td>
                        <td className={cellClass}>
                            <button
                                onClick={() => setEditForm(service)}
                                className="text-[#981D0E] hover:text-[#F5ABAB]"
                                aria-label={`Edit ${service.name}`}
                            >
                                <PencilIcon className="h-5 w-5" />
                            </button>
                        </td>
                    </>)}
                />

                {editForm && (
                    <div className="fixed z-[100000] inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <h2 className="text-xl font-semibold mb-4">Edit Service</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={editForm.name ?? ''}
                                            onChange={handleChange}
                                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Location</label>
                                        <select
                                            name="location"
                                            value={editForm.location ?? 'PHYSICAL'}
                                            onChange={handleChange}
                                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                                        >
                                            <option value="PHYSICAL">Physical</option>
                                            <option value="VIRTUAL">Virtual</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Type</label>
                                        <select
                                            name="type"
                                            value={editForm.type ?? 'INDIVIDUAL'}
                                            onChange={handleChange}
                                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                                        >
                                            <option value="INDIVIDUAL">Individual</option>
                                            <option value="GROUP">Group</option>
                                            <option value="ORGANIZATION">Organization</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Price</label>
                                        <input
                                            type="number"
                                            name="price"
                                            value={editForm.price ?? ''}
                                            onChange={handleChange}
                                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Sessions</label>
                                        <input
                                            type="number"
                                            name="sessions"
                                            value={editForm.sessions ?? ''}
                                            onChange={handleChange}
                                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                                        />
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            name="isExtra"
                                            id="isExtra"
                                            checked={Boolean(editForm.isExtra)}
                                            onChange={handleChange}
                                            className="h-4 w-4 rounded border-gray-300"
                                        />
                                        <label htmlFor="isExtra" className="ml-2 text-sm font-medium text-gray-700">Extra Service</label>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setEditForm(null)}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 bg-[#981D0E] text-white rounded-md text-sm font-medium hover:bg-[#F5ABAB] disabled:opacity-50"
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Section>
    </>);
}

export default Services;
